import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert, BadgeCheck, ExternalLink, XCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_REASON_LABELS = {
  possible_fraud: "Posible fraude", false_info: "Información falsa", discrimination: "Discriminación",
  inappropriate_content: "Contenido inapropiado", job_expired: "Empleo vencido", other: "Otro",
};

const VERIFICATION_REASON_LABELS = {
  missing_required_fields: "Faltan datos obligatorios",
  invalid_tax_id_format: "Formato de NIF/CIF/EIN inválido",
  duplicate_tax_id: "NIF/CIF/EIN ya usado por otra empresa",
  owner_has_suspended_company: "El propietario tiene una empresa suspendida",
};

// Trust & Safety MVP (launch-readiness sprint, sept-2026) — reportes de empleo
// + cola de verificación de empresa (needs_review). Las acciones de moderación
// reales (pausar empleo / suspender empresa) se hacen desde Empleos/Empresas.
export default function AdminReportsPage() {
  const [tab, setTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState("");
  const [notes, setNotes] = useState({});

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports`, { headers: getAdminAuthHeaders(), params: { status: "pending" } });
      setReports(res.data.reports || []);
    } finally { setLoading(false); }
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/companies/verification-queue`, { headers: getAdminAuthHeaders() });
      setCompanies(res.data.companies || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { tab === "reports" ? loadReports() : loadCompanies(); }, [tab, loadReports, loadCompanies]);

  const resolveReport = async (reportId, action) => {
    setActioning(reportId);
    try {
      await axios.post(`${API}/admin/reports/${reportId}/resolve`, { action, note: notes[reportId] || null }, { headers: getAdminAuthHeaders() });
      toast.success(action === "dismissed" ? "Reporte descartado" : "Reporte marcado como revisado");
      await loadReports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el reporte");
    } finally { setActioning(""); }
  };

  const reviewCompany = async (companyId, action) => {
    setActioning(companyId);
    try {
      await axios.post(`${API}/admin/companies/${companyId}/verification/review`, { action, note: notes[companyId] || null }, { headers: getAdminAuthHeaders() });
      toast.success("Verificación actualizada");
      await loadCompanies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar la verificación");
    } finally { setActioning(""); }
  };

  return (
    <div data-testid="admin-reports-page">
      <h1 className="text-xl font-bold text-slate-900 mb-1">Trust &amp; Safety</h1>
      <p className="text-sm text-slate-500 mb-4">Reportes de usuarios y verificación de empresas que necesitan revisión manual</p>

      <div className="flex gap-2 mb-5">
        <button data-testid="admin-reports-tab-reports" onClick={() => setTab("reports")}
          className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${tab === "reports" ? "bg-[#028CF8] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          Reportes de empleo
        </button>
        <button data-testid="admin-reports-tab-verification" onClick={() => setTab("verification")}
          className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${tab === "verification" ? "bg-[#028CF8] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
          Verificación de empresas
        </button>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}

      {!loading && tab === "reports" && (
        <div className="space-y-3" data-testid="admin-reports-list">
          {reports.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No hay reportes pendientes.</p>}
          {reports.map((r) => (
            <div key={r.report_id} data-testid={`admin-report-row-${r.report_id}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-red-500" />
                    <span className="font-semibold text-slate-900 text-sm">{r.job_title}</span>
                    <a href={`/jobs/${r.job_id}`} target="_blank" rel="noreferrer" className="text-[#028CF8]"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{r.company_name} · Motivo: <span className="font-semibold">{REPORT_REASON_LABELS[r.reason] || r.reason}</span></p>
                  {r.details && <p className="text-xs text-slate-600 mt-1 italic">"{r.details}"</p>}
                  <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Textarea data-testid={`admin-report-note-${r.report_id}`} placeholder="Nota interna (opcional)" value={notes[r.report_id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.report_id]: e.target.value }))} className="text-xs w-56 min-h-[50px]" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`admin-report-dismiss-${r.report_id}`} disabled={actioning === r.report_id} onClick={() => resolveReport(r.report_id, "dismissed")}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Descartar
                    </Button>
                    <Button size="sm" data-testid={`admin-report-reviewed-${r.report_id}`} disabled={actioning === r.report_id} onClick={() => resolveReport(r.report_id, "reviewed")} className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Marcar revisado
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "verification" && (
        <div className="space-y-3" data-testid="admin-verification-list">
          {companies.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No hay empresas pendientes de revisión.</p>}
          {companies.map((c) => (
            <div key={c.company_id} data-testid={`admin-verification-row-${c.company_id}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-4 w-4 text-amber-500" />
                    <span className="font-semibold text-slate-900 text-sm">{c.name}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c.legal_name || "Sin razón social"} · NIF/CIF/EIN: {c.tax_id || "—"} · {c.country_code}</p>
                  <ul className="text-xs text-amber-700 mt-1 list-disc pl-4">
                    {(c.verification_reasons || []).map((reason) => <li key={reason}>{VERIFICATION_REASON_LABELS[reason] || reason}</li>)}
                  </ul>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Textarea data-testid={`admin-verification-note-${c.company_id}`} placeholder="Nota interna (opcional)" value={notes[c.company_id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [c.company_id]: e.target.value }))} className="text-xs w-56 min-h-[50px]" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`admin-verification-correction-${c.company_id}`} disabled={actioning === c.company_id} onClick={() => reviewCompany(c.company_id, "request_correction")}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Pedir corrección
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`admin-verification-reject-${c.company_id}`} disabled={actioning === c.company_id} onClick={() => reviewCompany(c.company_id, "reject")} className="text-red-600 border-red-200 hover:bg-red-50">
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Rechazar
                    </Button>
                    <Button size="sm" data-testid={`admin-verification-approve-${c.company_id}`} disabled={actioning === c.company_id} onClick={() => reviewCompany(c.company_id, "approve")} className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Aprobar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
